import { useLocation } from "react-router-dom";
import { isPrerendering } from "./prerender";

/**
 * Whether the browser already has this view painted from prerendered HTML.
 *
 * `data-prerendered` stays on <html> for the whole session, so it alone can't
 * answer this — after a client-side navigation the attribute is still there but
 * the new route was rendered by React, not served as HTML. Pinning the path the
 * document actually loaded on distinguishes the two.
 */
const documentWasPrerendered =
  typeof document !== "undefined" &&
  document.documentElement.getAttribute("data-prerendered") === "true";

const initialPath =
  typeof window !== "undefined" ? window.location.pathname : "";

/**
 * Decides whether to show a loading spinner.
 *
 * The rule: only show one when the user is genuinely looking at nothing.
 *
 *  - Prerendered routes arrive fully painted, so a spinner there would hide
 *    content that is already on screen and make the page feel slower than it
 *    is. (This is why the old hard-coded `setTimeout` loaders were removed —
 *    they also made the prerenderer capture a spinner instead of the page.)
 *  - Routes that start from the empty app shell (/myprofile, /menuDetails/:id,
 *    /menubyCategory/:id) and client-side navigations that wait on an API call
 *    genuinely have nothing to display, so they do get the spinner.
 *  - Never during prerendering itself, or the snapshot would capture it.
 *
 * @param {boolean} isLoading whether the page's own data is still pending
 */
export function useContentLoader(isLoading) {
  const location = useLocation();

  if (!isLoading) return false;
  if (isPrerendering()) return false;

  const isPrerenderedInitialView =
    documentWasPrerendered && location.pathname === initialPath;

  return !isPrerenderedInitialView;
}

export default useContentLoader;
